import { IItemCacheContext, IThunkAction, IDispatch } from '../store/actions/ItemCacheActions';
import { unpack } from '../models/Facet';
import { offlineCacheFacet } from '../facets/ConfigurationFacets';

/**
 * @public
 */
export interface IEnsureGetJSONResponse {
    <TResponse>(key: string): Promise<TResponse>;
}

/**
 * @public
 */
export interface IEnsureSetJSONResponse {
    <TResponse>(key: string, response: TResponse): Promise<TResponse>;
}

/**
 * @public
 */
export interface IIsOffline {
    (): boolean;
}

/**
 * @public
 */
export interface IOfflineCache {
    ensureGetJSONResponse?: IEnsureGetJSONResponse;
    ensureSetJSONResponse?: IEnsureSetJSONResponse;
    isOffline?: IIsOffline;
    clearCache?: () => void;
}

/**
 * @public
 */
export function getJSONResponse<TResponse>(key: string): IThunkAction<Promise<TResponse | undefined>, {}, {}> {
    return async (dispatch: IDispatch<TResponse>) => {
        const ensureGetJSONResponse = dispatch(getEnsureJSONResponse());

        let jsonResponse: TResponse | undefined;

        if (ensureGetJSONResponse) {
            jsonResponse = await ensureGetJSONResponse<TResponse>(key);
        }
        return jsonResponse;
    };
}

function getEnsureJSONResponse(): IThunkAction<IEnsureGetJSONResponse, {}, IItemCacheContext> {
    return (dispatch: IDispatch<{}>, getState: () => {}, context: IItemCacheContext) => {
        let ensureGetJSONResponse: IEnsureGetJSONResponse = undefined!;

        if (context && context.configuration) {
            const {
                spDataSourceConfiguration
            } = unpack({
                spDataSourceConfiguration: offlineCacheFacet
            }, context.configuration);

            if (spDataSourceConfiguration && spDataSourceConfiguration.ensureGetJSONResponse) {
                ensureGetJSONResponse = spDataSourceConfiguration.ensureGetJSONResponse;
            }
        }
        return ensureGetJSONResponse;
    };
}

/**
 * @public
 */
export function setJSONResponse<TResponse>(key: string, response: TResponse): IThunkAction<Promise<TResponse
    | undefined>, {}, {}> {
    return async (dispatch: IDispatch<TResponse>) => {
        const ensureSetJSONResponse = dispatch(setEnsureJSONResponse());

        let jsonResponse: TResponse | undefined;

        if (ensureSetJSONResponse) {
            jsonResponse = await ensureSetJSONResponse<TResponse>(key, response);
        }
        return jsonResponse;
    };
}

function setEnsureJSONResponse(): IThunkAction<IEnsureSetJSONResponse, {}, IItemCacheContext> {
    return (dispatch: IDispatch<{}>, getState: () => {}, context: IItemCacheContext) => {
        let ensureSetJSONResponse: IEnsureSetJSONResponse = undefined!;

        if (context && context.configuration) {
            const {
                spDataSourceConfiguration
            } = unpack({
                spDataSourceConfiguration: offlineCacheFacet
            }, context.configuration);

            if (spDataSourceConfiguration && spDataSourceConfiguration.ensureSetJSONResponse) {
                ensureSetJSONResponse = spDataSourceConfiguration.ensureSetJSONResponse;
            }
        }
        return ensureSetJSONResponse;
    };
}

/**
 * @public
 */
export function isOffline(): IThunkAction<boolean, {}, {}> {
    return (dispatch: IDispatch<{}>) => {
        const ensureGetOfflineMode = dispatch(getEnsureOfflineMode());

        if (ensureGetOfflineMode) {
            return ensureGetOfflineMode();
        }
        return false;
    };
}

function getEnsureOfflineMode(): IThunkAction<IIsOffline, {}, IItemCacheContext> {
    return (dispatch: IDispatch<{}>, getState: () => {}, context: IItemCacheContext) => {
        let ensureGetOfflineMode: () => boolean = undefined!;

        if (context && context.configuration) {
            const {
                spDataSourceConfiguration
            } = unpack({
                spDataSourceConfiguration: offlineCacheFacet
            }, context.configuration);

            if (spDataSourceConfiguration && spDataSourceConfiguration.isOffline) {
                ensureGetOfflineMode = spDataSourceConfiguration.isOffline;
            }
        }
        return ensureGetOfflineMode;
    };
}

/**
 * @public
 */
export function clearCache(): IThunkAction<void, {}, IItemCacheContext> {
    return (dispatch: IDispatch<{}>, getState: () => {}, context: IItemCacheContext) => {

        if (context && context.configuration) {
            const { spDataSourceConfiguration } = unpack({
                spDataSourceConfiguration: offlineCacheFacet
            }, context.configuration);

            if (spDataSourceConfiguration && spDataSourceConfiguration.clearCache) {
                spDataSourceConfiguration.clearCache();
            }
        }
    };
}
